import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import PropertyCard from "@/components/PropertyCard";
import WhatsAppButton from "@/components/WhatsAppButton";
import LeadForm from "@/components/LeadForm";
import { PropertyMap } from "@/components/PropertyMap";
import { breadcrumbSchema, projectSchema, faqSchema } from "@/lib/schema";
import {
  MapPin, CheckCircle, Warning, House, Calendar, CurrencyDollar,
  Buildings, FilePdf, CaretDown,
} from "@phosphor-icons/react";

// Editorial fallback used for any project seeded/imported without a hero image.
const FALLBACK_HERO = "https://images.unsplash.com/photo-1518684079-3c830dcef090?w=1920";
const FALLBACK_DESC = "Detailed developer briefing is being prepared. Register your interest below and a Point Penta advisor will share the full sales pack — floor plates, unit mix, payment plan and amenity schedule — within one business day.";

function slugify(s) {
  return (s || "").toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
}

function defaultProjectFaqs(p) {
  return [
    {
      q: `What is the handover date for ${p.name}?`,
      a: p.handover
        ? `${p.name} is scheduled for handover in ${p.handover}. Timelines follow the developer's escrow milestones and are subject to RERA-registered progress.`
        : `Handover for ${p.name} will be confirmed at the sales launch. Register interest to be alerted the moment the developer publishes the escrow schedule.`,
    },
    {
      q: `What is the payment plan for ${p.name}?`,
      a: p.payment_plan
        ? `The developer's current plan is ${p.payment_plan}. Post-handover options and DLD-fee waivers may apply — your advisor will confirm what is live this week.`
        : `Payment plans vary by phase and unit type. Speak to your Point Penta advisor for the current offer, including any post-handover instalments.`,
    },
    {
      q: `Is ${p.name} eligible for the UAE Golden Visa?`,
      a: (p.starting_price || 0) >= 2000000
        ? `Yes — units in ${p.name} start above the AED 2M threshold, so buyers qualify for the 10-year Golden Visa. We can walk you through the residency application after booking.`
        : `Some units start below the AED 2M Golden Visa threshold. Larger configurations typically qualify — ask us to filter units that unlock 10-year residency.`,
    },
    {
      q: `Who is the developer of ${p.name}?`,
      a: p.developer?.name
        ? `${p.name} is delivered by ${p.developer.name}. Explore their full track record on our developer profile page linked below.`
        : `Developer details will be shared with the full sales pack. All Point Penta off-plan mandates are signed with Tier-1 UAE developers.`,
    },
  ];
}

export default function ProjectDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [idx, setIdx] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    setP(null); setNotFound(false); setIdx(0); setOpenFaq(0);
    api.get(`/projects/${slug}`)
      .then((r) => setP(r.data))
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
        else setP({ _error: true });
      });
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6" data-testid="project-not-found">
        <Warning size={40} className="text-[#001588] mb-4" weight="thin" />
        <h1 className="font-serif text-3xl mb-3">Project not found</h1>
        <p className="text-neutral-500 max-w-md mb-6">The project you&apos;re looking for isn&apos;t on our books yet. Browse the full catalogue below.</p>
        <Link to="/projects" className="px-6 py-3 bg-[#001588] text-white text-sm uppercase tracking-[0.2em]">All off-plan</Link>
      </div>
    );
  }
  if (!p) return <div className="min-h-[60vh] flex items-center justify-center text-neutral-500">Loading…</div>;
  if (p._error) return <div className="min-h-[60vh] flex items-center justify-center text-neutral-500">We couldn&apos;t load this project. Please try again.</div>;

  // Compose a gallery: hero + up to 3 gallery images (skip duplicates).
  const heroImage = p.hero_image || FALLBACK_HERO;
  const galleryPool = [heroImage, ...((p.gallery || []).filter((g) => g && g !== heroImage))];
  const images = galleryPool.length ? galleryPool : [FALLBACK_HERO];
  const description = p.description || FALLBACK_DESC;
  const faqs = (p.faqs && p.faqs.length) ? p.faqs : defaultProjectFaqs(p);

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Off-plan", path: "/projects" },
    ...(p.community ? [{ name: p.community, path: `/areas/${slugify(p.community)}` }] : []),
    { name: p.name, path: `/projects/${slug}` },
  ];

  const startingLabel = p.starting_price
    ? `from ${p.currency || "AED"} ${Number(p.starting_price).toLocaleString()}`
    : "Price on request";

  return (
    <div data-testid={`project-detail-${slug}`}>
      <SEO
        title={p.seo_title || p.name}
        description={(p.seo_description || description).slice(0, 160)}
        path={`/projects/${slug}`}
        image={heroImage}
        type="article"
        schema={[
          breadcrumbSchema(crumbs),
          projectSchema(p),
          faqSchema(faqs.map((f) => ({ q: f.q, a: f.a }))),
        ]}
      />

      {/* Breadcrumbs + gallery — mirrors PropertyDetail */}
      <section className="pt-6">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Breadcrumbs items={crumbs} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="lg:col-span-2 aspect-[16/10] bg-[#ffffff] pp-hover-zoom relative" data-testid="project-hero-image">
              <img
                src={resolveImage(images[idx]) || heroImage}
                alt={p.name}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.src = FALLBACK_HERO; }}
              />
              {p.featured && (
                <span className="absolute top-4 left-4 bg-[#001588] text-white text-[10px] tracking-[0.2em] uppercase px-2.5 py-1">
                  Featured
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-3">
              {images.slice(0, 3).map((im, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`aspect-[4/3] pp-hover-zoom relative ${idx === i ? "outline outline-2 outline-[#001588]" : ""}`}
                  data-testid={`project-gallery-thumb-${i}`}
                >
                  <img
                    src={resolveImage(im) || im}
                    alt={`${p.name} — gallery ${i + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.src = FALLBACK_HERO; }}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main content + sidebar */}
      <section className="py-14">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="overline text-[#001588] mb-3 flex items-center gap-2">
              <MapPin size={12} weight="fill" />
              {p.community}
              {p.sub_community ? ` · ${p.sub_community}` : ""}
              {p.emirate ? ` · ${p.emirate}` : ""}
            </div>
            <h1 className="font-serif text-4xl lg:text-5xl leading-tight mb-4" data-testid="project-title">{p.name}</h1>
            <div className="flex items-baseline gap-3 mb-6 flex-wrap">
              <span className="font-serif text-4xl text-[#001588]" data-testid="project-price">{startingLabel}</span>
              {p.status && (
                <span className="text-xs uppercase tracking-[0.2em] border border-[#001588] text-[#001588] px-3 py-1">
                  {p.status}
                </span>
              )}
            </div>

            {/* Media buttons */}
            {p.brochure_url && (
              <div className="flex flex-wrap gap-2 mb-8">
                <a
                  href={p.brochure_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="project-brochure-btn"
                  className="inline-flex items-center gap-2 border border-neutral-300 px-4 py-2 text-sm hover:border-[#001588] hover:text-[#001588] transition-colors"
                >
                  <FilePdf size={14} /> Brochure PDF
                </a>
              </div>
            )}

            {/* Meta strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-b border-neutral-200 py-6 mb-10">
              <Meta icon={House} label="Unit mix" value={p.unit_mix || "Various"} />
              <Meta icon={Calendar} label="Handover" value={p.handover || "TBA"} />
              <Meta icon={CurrencyDollar} label="Payment plan" value={p.payment_plan || "On enquiry"} />
              <Meta icon={Buildings} label="Status" value={p.status || "Off-plan"} />
            </div>

            {/* Overview */}
            <h2 className="font-serif text-3xl mb-4">Overview</h2>
            <p className={`leading-relaxed whitespace-pre-line mb-10 ${p.description ? "text-neutral-700" : "text-neutral-500 italic"}`}>
              {description}
            </p>

            {/* Unit types */}
            {p.unit_types?.length > 0 && (
              <>
                <h3 className="font-serif text-2xl mb-4">Unit types</h3>
                <div className="flex flex-wrap gap-2 mb-10">
                  {p.unit_types.map((u) => (
                    <span key={u} className="text-xs tracking-wider uppercase border border-neutral-300 px-3 py-1.5">{u}</span>
                  ))}
                </div>
              </>
            )}

            {/* Amenities */}
            {p.amenities?.length > 0 && (
              <>
                <h3 className="font-serif text-2xl mb-4">Amenities</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-10">
                  {p.amenities.map((a) => (
                    <div key={a} className="flex items-center gap-2 text-sm text-neutral-700">
                      <CheckCircle size={16} weight="fill" className="text-[#001588] flex-shrink-0" /> {a}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Nearby landmarks */}
            {p.nearby_landmarks?.length > 0 && (
              <>
                <h3 className="font-serif text-2xl mb-4">Nearby landmarks</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-10">
                  {p.nearby_landmarks.map((l) => (
                    <div key={l} className="flex items-center gap-2 text-sm text-neutral-700">
                      <MapPin size={14} weight="fill" className="text-[#001588] flex-shrink-0" /> {l}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Floor plans */}
            {p.floor_plans?.length > 0 && (
              <>
                <h3 className="font-serif text-2xl mb-4">Floor plans</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-10">
                  {p.floor_plans.map((fp, i) => (
                    <a
                      key={i}
                      href={fp}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="border border-neutral-200 aspect-[3/4] flex items-center justify-center hover:border-[#001588] transition-colors"
                      data-testid={`project-floorplan-${i}`}
                    >
                      <img
                        src={resolveImage(fp) || fp}
                        alt={`${p.name} floor plan ${i + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </a>
                  ))}
                </div>
              </>
            )}

            {/* Developer & Delivery */}
            {(p.developer || p.handover || p.payment_plan) && (
              <div className="border border-neutral-200 p-6 mb-10">
                <div className="overline text-[#001588] mb-4">Developer &amp; Delivery</div>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  {p.developer?.name && (
                    <div>
                      <div className="text-neutral-500 text-xs uppercase tracking-wider mb-1">Developer</div>
                      {p.developer.name}
                    </div>
                  )}
                  {p.handover && (
                    <div>
                      <div className="text-neutral-500 text-xs uppercase tracking-wider mb-1">Handover</div>
                      {p.handover}
                    </div>
                  )}
                  {p.payment_plan && (
                    <div>
                      <div className="text-neutral-500 text-xs uppercase tracking-wider mb-1">Payment plan</div>
                      {p.payment_plan}
                    </div>
                  )}
                </div>
                {p.developer?.slug && (
                  <div className="mt-4 pt-4 border-t border-neutral-200 flex flex-wrap gap-4 text-sm">
                    <Link
                      to={`/developers/${p.developer.slug}`}
                      className="text-[#001588] hover:underline"
                      data-testid="project-developer-link"
                    >
                      More by {p.developer.name} →
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Location */}
            {(p.latitude && p.longitude) ? (
              <>
                <h3 className="font-serif text-2xl mb-4">Location</h3>
                <PropertyMap lat={p.latitude} lng={p.longitude} community={p.community} title={p.name} />
                <div className="text-xs text-neutral-500 mt-3">{p.community}{p.sub_community ? ` · ${p.sub_community}` : ""}, {p.emirate || "Dubai"}</div>
              </>
            ) : null}

            {/* FAQs */}
            <h3 className="font-serif text-2xl mb-4 mt-12">Frequently asked</h3>
            <div className="border-t border-neutral-200">
              {faqs.map((f, i) => (
                <button
                  key={i}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full text-left border-b border-neutral-200 py-5"
                  data-testid={`project-faq-${i}`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-serif text-lg text-neutral-900">{f.q}</span>
                    <CaretDown size={16} className={`transition-transform ${openFaq === i ? "rotate-180" : ""} text-[#001588]`} />
                  </div>
                  {openFaq === i && <div className="text-neutral-700 mt-3 leading-relaxed">{f.a}</div>}
                </button>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-28 space-y-4">
              <div className="border border-neutral-200 p-6 space-y-3" data-testid="project-quick-facts">
                <div className="overline text-[#001588] mb-2">At a glance</div>
                <Stat label="Community" value={p.community} />
                <Stat label="Emirate" value={p.emirate} />
                <Stat label="Starting price" value={startingLabel} />
                <Stat label="Unit mix" value={p.unit_mix} />
                <Stat label="Payment plan" value={p.payment_plan} />
                <Stat label="Handover" value={p.handover} />
              </div>
              <WhatsAppButton
                className="w-full"
                message={`Hi Point Penta, I'd like priority allocation on ${p.name}.`}
                testId={`whatsapp-project-${slug}`}
              />
              <div className="border border-neutral-200 p-6">
                <div className="overline text-[#001588] mb-3">Register interest</div>
                <LeadForm
                  source={`project-${slug}`}
                  interest="off-plan"
                  testIdPrefix={`project-lead-${slug}`}
                  submitLabel="Register interest"
                />
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Current mandates in the project (unchanged) */}
      {p.listings?.length > 0 && (
        <section className="pb-16">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <h2 className="font-serif text-3xl sm:text-4xl mb-8">Current mandates in {p.name}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-14">
              {p.listings.map((l) => <PropertyCard key={l.id} p={l} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500 mb-1"><Icon size={14} /> {label}</div>
      <div className="text-lg text-neutral-900 font-serif capitalize">{value || "—"}</div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex justify-between items-baseline border-b border-neutral-200 pb-2 text-sm gap-3">
      <span className="text-neutral-500 uppercase tracking-wider text-xs flex-shrink-0">{label}</span>
      <span className="text-neutral-800 text-right max-w-[60%]">{value || "—"}</span>
    </div>
  );
}
